import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import FluidSubpageHero from '../components/FluidSubpageHero';

export default function MistankeAdhd() {
  const { lang, localizePath } = useLanguage();

  // SEO Metadata
  const seoTitle = lang === 'da'
    ? 'Mistanke om ADHD? Tag den officielle ASRS-v1.1 selvtest | Nordisk Psykiatri'
    : 'Suspect ADHD? Take the official ASRS-v1.1 self-test | Nordisk Psykiatri';
  const seoDesc = lang === 'da'
    ? 'Oplever du koncentrationsbesvær, rastløshed eller organisatoriske udfordringer? Tag den officielle WHO ASRS-v1.1 selvtest for voksne her.'
    : 'Experiencing concentration difficulties, restlessness or organisational challenges? Take the official WHO ASRS-v1.1 self-test for adults here.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  // State
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(new Array(18).fill(null));
  const [showResults, setShowResults] = useState(false);

  // Scroll to screening section
  const startScreening = () => {
    const el = document.getElementById('screening-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Questions from the asset file
  const questions = [
    {
      id: 1,
      text: {
        da: "Hvor ofte har du svært ved at få tingene i orden, når du skal lave en opgave, der kræver organisering?",
        sv: "Hur ofta har du svårt att få ordning på saker och ting när du ska utföra en uppgift som kräver organisation?"
      },
      part: 'A',
      isPositive: (val) => val >= 2 // Nogle gange, Ofte, Meget ofte
    },
    {
      id: 2,
      text: {
        da: "Hvor ofte har du problemer med at huske aftaler eller forpligtelser?",
        sv: "Hur ofta har du svårt att komma ihåg avtalade möten eller åtaganden?"
      },
      part: 'A',
      isPositive: (val) => val >= 2
    },
    {
      id: 3,
      text: {
        da: "Hvor ofte undgår eller udsætter du opgaver, der kræver længere tids mental anstrengelse?",
        sv: "Hur ofta undviker eller skjuter du upp uppgifter som kräver långvarig mental ansträngning?"
      },
      part: 'A',
      isPositive: (val) => val >= 3 // Ofte, Meget ofte
    },
    {
      id: 4,
      text: {
        da: "Hvor ofte har du svært ved at holde opmærksomheden ved rutineprægede opgaver?",
        sv: "Hur ofta har du svårt att hålla uppmärksamheten vid rutinmässiga uppgifter?"
      },
      part: 'A',
      isPositive: (val) => val >= 2
    },
    {
      id: 5,
      text: {
        da: "Hvor ofte bliver du distraheret af aktivitet eller støj omkring dig?",
        sv: "Hur ofta blir du distraherad av aktivitet eller buller omkring dig?"
      },
      part: 'A',
      isPositive: (val) => val >= 3
    },
    {
      id: 6,
      text: {
        da: "Hvor ofte føler du dig rastløs eller har svært ved at sidde stille?",
        sv: "Hur ofta känner du dig rastlös eller har svårt att sitta still?"
      },
      part: 'A',
      isPositive: (val) => val >= 3
    },
    {
      id: 7,
      text: {
        da: "Hvor ofte afbryder du andre, mens de taler?",
        sv: "Hur ofta avbryter du andra medan de talar?"
      },
      part: 'B',
      isPositive: (val) => val >= 3
    },
    {
      id: 8,
      text: {
        da: "Hvor ofte har du svært ved at vente på din tur?",
        sv: "Hur ofta har du svårt att vänta på din tur?"
      },
      part: 'B',
      isPositive: (val) => val >= 3
    },
    {
      id: 9,
      text: {
        da: "Hvor ofte mister du ting, du har brug for i hverdagen?",
        sv: "Hur ofta tappar du bort saker som du behöver i vardagen?"
      },
      part: 'B',
      isPositive: (val) => val >= 3
    },
    {
      id: 10,
      text: {
        da: "Hvor ofte laver du fejl, fordi du overser detaljer?",
        sv: "Hur ofta gör du misstag för att du förbiser detaljer?"
      },
      part: 'B',
      isPositive: (val) => val >= 2
    },
    {
      id: 11,
      text: {
        da: "Hvor ofte har du svært ved at afslutte en opgave, selv om de svære dele er overstået?",
        sv: "Hur ofta har du svårt att avsluta en uppgift, även om de svåra delarna är klara?"
      },
      part: 'B',
      isPositive: (val) => val >= 2
    },
    {
      id: 12,
      text: {
        da: "Hvor ofte skifter du aktivitet, før du er færdig med den første?",
        sv: "Hur ofta byter du aktivitet innan du är klar med den första?"
      },
      part: 'B',
      isPositive: (val) => val >= 2
    },
    {
      id: 13,
      text: {
        da: "Hvor ofte føler du dig drevet af en indre motor?",
        sv: "Hur ofta känner du dig driven av en inre motor?"
      },
      part: 'C',
      isPositive: (val) => val >= 3
    },
    {
      id: 14,
      text: {
        da: "Hvor ofte taler du mere end andre omkring dig?",
        sv: "Hur ofta talar du mer än andra runt omkring dig?"
      },
      part: 'C',
      isPositive: (val) => val >= 3
    },
    {
      id: 15,
      text: {
        da: "Hvor ofte handler du, før du når at tænke dig om?",
        sv: "Hur ofta agerar du innan du hinner tänka efter?"
      },
      part: 'C',
      isPositive: (val) => val >= 3
    },
    {
      id: 16,
      text: {
        da: "Hvor ofte har du svært ved at slappe af?",
        sv: "Hur ofta har du svårt att slappna av?"
      },
      part: 'C',
      isPositive: (val) => val >= 3
    },
    {
      id: 17,
      text: {
        da: "Hvor ofte oplever du, at dine tanker springer hurtigt fra emne til emne?",
        sv: "Hur ofta upplever du att dina tankar hoppar snabbt från ämne till ämne?"
      },
      part: 'C',
      isPositive: (val) => val >= 3
    },
    {
      id: 18,
      text: {
        da: "Hvor ofte påvirker disse udfordringer dit arbejde, studie eller dine relationer?",
        sv: "Hur ofta påverkar dessa utmaningar ditt arbete, dina studier eller dina relationer?"
      },
      part: 'C',
      isPositive: (val) => val >= 2
    }
  ];

  const options = lang === 'da'
    ? [
        { label: 'Aldrig', value: 0 },
        { label: 'Sjældent', value: 1 },
        { label: 'Nogle gange', value: 2 },
        { label: 'Ofte', value: 3 },
        { label: 'Meget ofte', value: 4 }
      ]
    : [
        { label: 'Never', value: 0 },
        { label: 'Rarely', value: 1 },
        { label: 'Sometimes', value: 2 },
        { label: 'Often', value: 3 },
        { label: 'Very often', value: 4 }
      ];

  const handleAnswer = (val) => {
    const newAnswers = [...answers];
    newAnswers[current] = val;
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (current < questions.length - 1) {
      setCurrent(current + 1);
    } else {
      setShowResults(true);
      setTimeout(() => {
        const el = document.getElementById('results-section');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handlePrev = () => {
    if (current > 0) {
      setCurrent(current - 1);
    }
  };

  const resetTest = () => {
    setAnswers(new Array(18).fill(null));
    setCurrent(0);
    setShowResults(false);
    startScreening();
  };

  // Calculate scores
  const totalScore = answers.reduce((sum, val) => sum + (val ?? 0), 0);
  const answeredCount = answers.filter(v => v !== null).length;
  const percentComplete = Math.round(((current + 1) / 18) * 100);

  // Part A positive count (official WHO screening threshold)
  const partAPositiveCount = questions
    .slice(0, 6)
    .filter((q, idx) => answers[idx] !== null && q.isPositive(answers[idx])).length;

  // Score category based on the asset file
  let scoreCategory = 'low';
  if (totalScore >= 24) {
    scoreCategory = 'high';
  } else if (totalScore >= 17) {
    scoreCategory = 'medium';
  }

  // Print results
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="bg-sand-light min-h-screen">
      {/* Hero Section */}
      <FluidSubpageHero
        badge={(
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-petrol-dark/10 text-petrol-dark text-xs font-semibold tracking-wider uppercase w-fit">
            {lang === 'da' ? 'VIDEN / ADHD-SCREENING (ASRS)' : 'KNOWLEDGE / ADHD SCREENING (ASRS)'}
          </div>
        )}
        title={lang === 'da' ? 'Mistanke om ADHD?' : 'Suspect ADHD?'}
        description={lang === 'da'
          ? 'ASRS-v1.1 (Adult ADHD Self-Report Scale) er et internationalt anerkendt screeningsredskab, som kan indikere, om dine symptomer kan være forenelige med ADHD. Screeningen kan ikke bruges til at stille en diagnose, men kan være et godt udgangspunkt for en faglig vurdering.'
          : 'ASRS-v1.1 (Adult ADHD Self-Report Scale) is an internationally recognised screening tool that can indicate whether your symptoms may be compatible with ADHD. The screening cannot be used to make a diagnosis, but can serve as a helpful starting point for a professional evaluation.'}
      >
        {/* Facts Grid & CTA */}
        <div className="mt-6">
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="flex items-center gap-2.5 bg-sand-medium/20 border border-sand-medium/30 rounded-xl p-2.5">
              <div className="w-8 h-8 rounded-full bg-sand-medium/40 flex items-center justify-center text-petrol-dark shrink-0">
                <i className="fa-regular fa-clock text-xs" />
              </div>
              <div className="text-xs font-semibold text-text-dark leading-tight">
                {lang === 'da' ? 'Tager ca. 8-10 min' : 'Takes approx. 8-10 min'}
              </div>
            </div>
            <div className="flex items-center gap-2.5 bg-sand-medium/20 border border-sand-medium/30 rounded-xl p-2.5">
              <div className="w-8 h-8 rounded-full bg-sand-medium/40 flex items-center justify-center text-petrol-dark shrink-0">
                <i className="fa-regular fa-shield-check text-xs" />
              </div>
              <div className="text-xs font-semibold text-text-dark leading-tight">
                {lang === 'da' ? 'Videnskabeligt valideret' : 'Scientifically validated'}
              </div>
            </div>
            <div className="flex items-center gap-2.5 bg-sand-medium/20 border border-sand-medium/30 rounded-xl p-2.5">
              <div className="w-8 h-8 rounded-full bg-sand-medium/40 flex items-center justify-center text-petrol-dark shrink-0">
                <i className="fa-regular fa-user-shield text-xs" />
              </div>
              <div className="text-xs font-semibold text-text-dark leading-tight">
                {lang === 'da' ? 'Anonymt & fortroligt' : 'Anonymous & confidential'}
              </div>
            </div>
            <div className="flex items-center gap-2.5 bg-sand-medium/20 border border-sand-medium/30 rounded-xl p-2.5">
              <div className="w-8 h-8 rounded-full bg-sand-medium/40 flex items-center justify-center text-petrol-dark shrink-0">
                <i className="fa-regular fa-list-ol text-xs" />
              </div>
              <div className="text-xs font-semibold text-text-dark leading-tight">
                {lang === 'da' ? '18 spørgsmål' : '18 questions'}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
            <button
              onClick={startScreening}
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-petrol-dark hover:bg-petrol-medium text-white rounded-full font-semibold text-sm transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer"
            >
              {lang === 'da' ? 'Start screeningen' : 'Start screening'} <i className="fa-solid fa-arrow-right" />
            </button>
            <span className="text-xs text-text-muted">
              {lang === 'da'
                ? 'Du behøver ikke oprette en bruger, og vi gemmer ikke dine svar.'
                : 'No registration needed, and your answers are not stored.'}
            </span>
          </div>
        </div>
      </FluidSubpageHero>

      {/* Screening Section */}
      <main id="screening-section" className="mx-auto max-w-[1180px] px-6 py-16 md:py-24">
        <div className="max-w-3xl mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-normal text-petrol-dark tracking-tight mb-4">
            {lang === 'da' ? 'Screeningen består af 18 spørgsmål' : 'Screeningen består av 18 frågor'}
          </h2>
          <p className="text-text-muted leading-relaxed">
            {lang === 'da'
              ? 'Besvar alle spørgsmål så ærligt som muligt ud fra, hvordan du har haft det inden for de sidste 6 måneder.'
              : 'Besvara alla frågor så ärligt som möjligt utifrån hur du har haft det under de senaste 6 månaderna.'}
          </p>
        </div>

        {!showResults ? (
          <div className="space-y-16">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Question Panel */}
            <div className="lg:col-span-2 border border-sand-medium/30 bg-white rounded-lg p-6 sm:p-8 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-sand-medium/20">
                <span className="text-sm font-semibold text-text-muted">
                  {lang === 'da' ? `Spørgsmål ${current + 1} af 18` : `Fråga ${current + 1} av 18`}
                </span>
                <div className="flex items-center gap-3 flex-grow max-w-xs sm:justify-end">
                  <div className="h-2 bg-sand-medium/20 rounded-full flex-grow overflow-hidden">
                    <div
                      className="h-full bg-petrol-medium rounded-full transition-all duration-300"
                      style={{ width: `${percentComplete}%` }}
                    />
                  </div>
                  <span className="text-xs font-bold text-petrol-dark shrink-0">{percentComplete}%</span>
                </div>
              </div>

              <h3 className="font-display text-2xl sm:text-3xl font-normal text-petrol-dark leading-tight mb-8">
                {questions[current].text[lang]}
              </h3>

              {/* Answers */}
              <div className="grid gap-3 mb-8">
                {options.map((opt) => {
                  const isChecked = answers[current] === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-4 border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                        isChecked
                          ? 'border-petrol-medium bg-petrol-medium/5 text-petrol-dark font-semibold'
                          : 'border-sand-medium/30 bg-white hover:border-petrol-medium/40 hover:bg-sand-light/10 text-text-dark'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`q-${current}`}
                        value={opt.value}
                        checked={isChecked}
                        onChange={() => handleAnswer(opt.value)}
                        className="w-4 h-4 text-petrol-medium focus:ring-petrol-medium border-sand-medium/40"
                      />
                      <span className="text-sm sm:text-base">{opt.label}</span>
                    </label>
                  );
                })}
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-sand-medium/20">
                <button
                  onClick={handlePrev}
                  disabled={current === 0}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 border border-sand-medium/40 rounded-full text-sm font-semibold transition-all duration-200 ${
                    current === 0
                      ? 'opacity-40 cursor-not-allowed text-text-muted'
                      : 'hover:bg-sand-light/30 text-text-dark cursor-pointer'
                  }`}
                >
                  <i className="fa-solid fa-arrow-left" /> {lang === 'da' ? 'Tilbage' : 'Tillbaka'}
                </button>

                <button
                  onClick={handleNext}
                  disabled={answers[current] === null}
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 ${
                    answers[current] === null
                      ? 'bg-sand-medium/30 text-text-muted cursor-not-allowed'
                      : 'bg-petrol-dark hover:bg-petrol-medium text-white shadow-sm cursor-pointer'
                  }`}
                >
                  {current === questions.length - 1
                    ? (lang === 'da' ? 'Se resultat' : 'Se resultat')
                    : (lang === 'da' ? 'Næste' : 'Nästa')}{' '}
                  <i className="fa-solid fa-arrow-right" />
                </button>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="border border-sand-medium/30 bg-white rounded-lg p-6 sm:p-8 shadow-sm">
              <h3 className="font-display text-xl font-normal text-petrol-dark mb-4">
                {lang === 'da' ? 'Fremgang' : 'Framsteg'}
              </h3>
              <p className="text-sm font-semibold text-text-dark mb-2">
                {lang === 'da'
                  ? `${answeredCount} af 18 besvaret`
                  : `${answeredCount} av 18 besvarade`}
              </p>
              <div className="h-1.5 bg-sand-medium/20 rounded-full overflow-hidden mb-8">
                <div
                  className="h-full bg-petrol-medium rounded-full transition-all duration-300"
                  style={{ width: `${(answeredCount / 18) * 100}%` }}
                />
              </div>

              {/* Steps */}
              <div className="space-y-6">
                <div className={`flex gap-3 text-xs sm:text-sm ${current < 6 ? 'text-petrol-dark font-semibold' : 'text-text-muted'}`}>
                  <div className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${current < 6 ? 'border-petrol-medium bg-petrol-medium/10' : 'border-sand-medium/40'}`}>
                    1
                  </div>
                  <div>
                    <div className="font-bold">{lang === 'da' ? 'Del A – Screening' : 'Del A – Screening'}</div>
                    <div className="text-xs text-text-muted mt-0.5">
                      {lang === 'da'
                        ? 'De første 6 spørgsmål bruges til at vurdere, om screeningsresultatet er positivt.'
                        : 'De första 6 frågorna används för att bedöma om screeningsresultatet är positivt.'}
                    </div>
                  </div>
                </div>

                <div className={`flex gap-3 text-xs sm:text-sm ${current >= 6 && current < 12 ? 'text-petrol-dark font-semibold' : 'text-text-muted'}`}>
                  <div className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${current >= 6 && current < 12 ? 'border-petrol-medium bg-petrol-medium/10' : 'border-sand-medium/40'}`}>
                    2
                  </div>
                  <div>
                    <div className="font-bold">{lang === 'da' ? 'Del B – Opmærksomhed' : 'Del B – Uppmärksamhet'}</div>
                    <div className="text-xs text-text-muted mt-0.5">
                      {lang === 'da'
                        ? 'Spørgsmål 7–12 handler om opmærksomhedsrelaterede udfordringer.'
                        : 'Frågorna 7–12 handlar om uppmärksamhetsrelaterade utmaningar.'}
                    </div>
                  </div>
                </div>

                <div className={`flex gap-3 text-xs sm:text-sm ${current >= 12 ? 'text-petrol-dark font-semibold' : 'text-text-muted'}`}>
                  <div className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${current >= 12 ? 'border-petrol-medium bg-petrol-medium/10' : 'border-sand-medium/40'}`}>
                    3
                  </div>
                  <div>
                    <div className="font-bold">{lang === 'da' ? 'Del C – Hyperaktivitet' : 'Del C – Hyperaktivitet'}</div>
                    <div className="text-xs text-text-muted mt-0.5">
                      {lang === 'da'
                        ? 'Spørgsmål 13–18 handler om hyperaktivitet og impulsivitet.'
                        : 'Frågorna 13–18 handlar om hyperaktivitet och impulsivitet.'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-sand-medium/20 text-xs text-text-muted flex items-center gap-2">
                <i className="fa-solid fa-lock text-emerald-600" />
                <span>
                  {lang === 'da'
                    ? 'Dine svar behandles lokalt i din browser og gemmes ikke.'
                    : 'Dina svar behandlas lokalt i din webbläsare och sparas inte.'}
                </span>
              </div>
            </aside>
          </div>

          {/* "Hvad betyder resultatet?" Section */}
          <div className="border-t border-sand-medium/20 pt-16">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
              {/* Left Column: Score Explanation */}
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <h2 className="font-display text-3xl font-normal text-petrol-dark tracking-tight mb-4">
                    {lang === 'da' ? 'Hvad betyder resultatet?' : 'Vad betyder resultatet?'}
                  </h2>
                  <p className="text-text-muted leading-relaxed">
                    {lang === 'da'
                      ? 'Når du har besvaret alle 18 spørgsmål, får du et resultat baseret på ASRS-v1.1 scoringssystemet.'
                      : 'När du har besvarat alla 18 frågor får du ett resultat baserat på ASRS-v1.1 poängsystem.'}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Lav score */}
                  <article className="border border-emerald-500/10 bg-emerald-50/10 rounded-lg p-6 flex flex-col justify-between h-full hover:border-emerald-500/20 transition-all duration-300">
                    <div>
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-700 mb-4">
                        <i className="fa-solid fa-check text-lg" />
                      </div>
                      <h3 className="font-display text-xl font-normal text-petrol-dark mb-1">
                        {lang === 'da' ? 'Lav score' : 'Låg poäng'}
                      </h3>
                      <span className="text-xs font-semibold text-text-muted block mb-4">
                        {lang === 'da' ? '(0-16 point)' : '(0-16 poäng)'}
                      </span>
                      <p className="text-sm text-text-muted leading-relaxed mb-4">
                        {lang === 'da'
                          ? 'Dine svar tyder ikke på ADHD-symptomer over screeningsgrænsen.'
                          : 'Dina svar tyder inte på ADHD-symptom över screeningsgränsen.'}
                      </p>
                    </div>
                    <p className="text-xs text-text-muted/80 italic border-t border-sand-medium/10 pt-4">
                      {lang === 'da'
                        ? 'Det udelukker ikke ADHD. Søg faglig vurdering, hvis du oplever udfordringer i din hverdag.'
                        : 'Det utesluter inte ADHD. Sök professionell bedömning om du upplever utmaningar i din vardag.'}
                    </p>
                  </article>

                  {/* Moderat score */}
                  <article className="border border-amber-500/10 bg-amber-50/10 rounded-lg p-6 flex flex-col justify-between h-full hover:border-amber-500/20 transition-all duration-300">
                    <div>
                      <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-700 mb-4">
                        <i className="fa-solid fa-exclamation text-lg" />
                      </div>
                      <h3 className="font-display text-xl font-normal text-petrol-dark mb-1">
                        {lang === 'da' ? 'Moderat score' : 'Moderat poäng'}
                      </h3>
                      <span className="text-xs font-semibold text-text-muted block mb-4">
                        {lang === 'da' ? '(17-23 point)' : '(17-23 poäng)'}
                      </span>
                      <p className="text-sm text-text-muted leading-relaxed mb-4">
                        {lang === 'da'
                          ? 'Dine svar tyder på, at du kan have ADHD-symptomer.'
                          : 'Dina svar tyder på att du kan ha ADHD-symptom.'}
                      </p>
                    </div>
                    <p className="text-xs text-text-muted/80 italic border-t border-sand-medium/10 pt-4">
                      {lang === 'da'
                        ? 'Overvej at tale med en fagperson for at få en grundigere vurdering.'
                        : 'Överväg att prata med en professionell för att få en mer djupgående bedömning.'}
                    </p>
                  </article>

                  {/* Høj score */}
                  <article className="border border-rose-500/10 bg-rose-50/10 rounded-lg p-6 flex flex-col justify-between h-full hover:border-rose-500/20 transition-all duration-300">
                    <div>
                      <div className="w-10 h-10 rounded-full bg-rose-500/10 flex items-center justify-center text-rose-700 mb-4">
                        <i className="fa-solid fa-triangle-exclamation text-lg" />
                      </div>
                      <h3 className="font-display text-xl font-normal text-petrol-dark mb-1">
                        {lang === 'da' ? 'Høj score' : 'Hög poäng'}
                      </h3>
                      <span className="text-xs font-semibold text-text-muted block mb-4">
                        {lang === 'da' ? '(≥ 24 point)' : '(≥ 24 poäng)'}
                      </span>
                      <p className="text-sm text-text-muted leading-relaxed mb-4">
                        {lang === 'da'
                          ? 'Dine svar tyder på, at du har flere symptomer, som kan være forenelige med ADHD.'
                          : 'Dina svar tyder på att du har flera symptom som kan vara förenliga med ADHD.'}
                      </p>
                    </div>
                    <p className="text-xs text-text-muted/80 italic border-t border-sand-medium/10 pt-4">
                      {lang === 'da'
                        ? 'Vi anbefaler en professionel udredning for at få klarhed over din situation.'
                        : 'Vi rekommenderar en professionell utredning för att få klarhet i din situation.'}
                    </p>
                  </article>
                </div>

                {/* Info Bar */}
                <div className="mt-8 border border-sand-medium/30 bg-[#faf8f5] rounded-lg p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-sand-medium/30 flex items-center justify-center text-petrol-dark shrink-0">
                      <i className="fa-solid fa-circle-info text-base" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-text-dark mb-1">
                        {lang === 'da' ? 'Vigtigt at vide' : 'Viktigt att veta'}
                      </h3>
                      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
                        {lang === 'da'
                          ? 'ASRS er et screeningsredskab og kan ikke stå alene som grundlag for en diagnose. Mange andre tilstande kan give lignende symptomer.'
                          : 'ASRS är ett screeningsverktyg och kan inte stå ensamt som grundlag för en diagnos. Många andra tillstånd kan ge liknande symptom.'}
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/adhd"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-sand-medium/40 rounded-full text-sm font-semibold text-text-dark hover:bg-sand-light/30 transition-all shrink-0 cursor-pointer"
                  >
                    {lang === 'da' ? 'Læs mere om ADHD' : 'Läs mer om ADHD'}
                  </Link>
                </div>
              </div>

              {/* Right Column: After Screening & CTA */}
              <div className="space-y-6">
                <div className="border border-sand-medium/30 bg-white rounded-lg p-6 sm:p-8 shadow-sm">
                  <h3 className="font-display text-2xl font-normal text-petrol-dark mb-6">
                    {lang === 'da' ? 'Efter screeningen' : 'Efter screeningen'}
                  </h3>
                  <ul className="space-y-4 text-sm text-text-muted mb-8">
                    <li className="flex items-start gap-3">
                      <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0" />
                      <span>
                        {lang === 'da'
                          ? 'Du får et samlet resultat og en kort forklaring'
                          : 'Du får ett samlat resultat och en kort förklaring'}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0" />
                      <span>
                        {lang === 'da'
                          ? 'Se hvordan dine svar fordeler sig på de tre områder'
                          : 'Se hur dina svar fördelar sig på de tre områdena'}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0" />
                      <span>
                        {lang === 'da'
                          ? 'Mulighed for at downloade eller printe din rapport'
                          : 'Möjlighet att ladda ner eller skriva ut din rapport'}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0" />
                      <span>
                        {lang === 'da'
                          ? 'Vejledning til næste skridt og behandlingsmuligheder'
                          : 'Vägledning för nästa steg och behandlingsalternativ'}
                      </span>
                    </li>
                  </ul>

                  <div className="bg-petrol-dark text-white rounded-lg p-6">
                    <h4 className="font-display text-xl font-normal mb-3">
                      {lang === 'da' ? 'Ønsker du en faglig vurdering?' : 'Would you like a professional assessment?'}
                    </h4>
                    <p className="text-xs text-white/80 leading-relaxed mb-6">
                      {lang === 'da'
                        ? 'Book en indledende samtale, så vi sammen kan finde ud af, om en udredning er det rette for dig.'
                        : 'Book an initial consultation so we can determine together if an assessment is right for you.'}
                    </p>
                    <Link
                      to={localizePath('/kontakt')}
                      className="inline-flex items-center gap-2 px-5 py-3 bg-white text-petrol-dark hover:bg-sand-light rounded-full font-semibold text-xs transition-all duration-200 shadow-sm"
                    >
                      {lang === 'da' ? 'Book indledende samtale' : 'Book initial consultation'} <i className="fa-solid fa-arrow-right" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
          /* Results Panel */
          <div id="results-section" className="space-y-8 print:space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
              {/* Left Column: Result Cards */}
              <div className="lg:col-span-2 space-y-6">
                <div className="border border-sand-medium/30 bg-white rounded-lg p-6 sm:p-8 shadow-sm">
                  <div className="flex justify-between items-center mb-6 pb-4 border-b border-sand-medium/20 print:mb-4">
                    <h3 className="font-display text-2xl font-normal text-petrol-dark">
                      {lang === 'da' ? 'Dit screeningsresultat' : 'Your screening result'}
                    </h3>
                    <button
                      onClick={handlePrint}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-sand-medium/40 rounded-full text-xs font-semibold text-text-dark hover:bg-sand-light/30 transition-all cursor-pointer print:hidden"
                    >
                      <i className="fa-solid fa-print" /> {lang === 'da' ? 'Print rapport' : 'Print report'}
                    </button>
                  </div>

                  {/* Score category display */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div
                      className={`border rounded-lg p-5 transition-all duration-300 ${
                        scoreCategory === 'low'
                          ? 'border-emerald-500 bg-emerald-50/40 text-emerald-950'
                          : 'border-sand-medium/20 bg-white opacity-60'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold uppercase tracking-wider">
                          {lang === 'da' ? 'Lav score' : 'Low score'}
                        </span>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${scoreCategory === 'low' ? 'bg-emerald-500 text-white' : 'bg-sand-medium/20 text-text-muted'}`}>
                          ✓
                        </div>
                      </div>
                      <h4 className="font-display text-lg font-normal mb-1">0 - 16 {lang === 'da' ? 'point' : 'points'}</h4>
                      <p className="text-xs leading-relaxed">
                        {lang === 'da'
                          ? 'Dine svar tyder ikke på ADHD-symptomer over screeningsgrænsen.'
                          : 'Your answers do not suggest ADHD symptoms above the screening threshold.'}
                      </p>
                    </div>

                    <div
                      className={`border rounded-lg p-5 transition-all duration-300 ${
                        scoreCategory === 'medium'
                          ? 'border-amber-500 bg-amber-50/40 text-amber-950'
                          : 'border-sand-medium/20 bg-white opacity-60'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold uppercase tracking-wider">
                          {lang === 'da' ? 'Moderat score' : 'Moderate score'}
                        </span>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${scoreCategory === 'medium' ? 'bg-amber-500 text-white' : 'bg-sand-medium/20 text-text-muted'}`}>
                          !
                        </div>
                      </div>
                      <h4 className="font-display text-lg font-normal mb-1">17 - 23 {lang === 'da' ? 'point' : 'points'}</h4>
                      <p className="text-xs leading-relaxed">
                        {lang === 'da'
                          ? 'Dine svar tyder på, at du kan have ADHD-symptomer.'
                          : 'Your answers suggest you may experience some ADHD symptoms.'}
                      </p>
                    </div>

                    <div
                      className={`border rounded-lg p-5 transition-all duration-300 ${
                        scoreCategory === 'high'
                          ? 'border-rose-500 bg-rose-50/40 text-rose-950'
                          : 'border-sand-medium/20 bg-white opacity-60'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold uppercase tracking-wider">
                          {lang === 'da' ? 'Høj score' : 'High score'}
                        </span>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${scoreCategory === 'high' ? 'bg-rose-500 text-white' : 'bg-sand-medium/20 text-text-muted'}`}>
                          △
                        </div>
                      </div>
                      <h4 className="font-display text-lg font-normal mb-1">≥ 24 {lang === 'da' ? 'point' : 'points'}</h4>
                      <p className="text-xs leading-relaxed">
                        {lang === 'da'
                          ? 'Dine svar tyder på, at du har flere symptomer, som kan være forenelige med ADHD.'
                          : 'Your answers indicate multiple symptoms that may be compatible with ADHD.'}
                      </p>
                    </div>
                  </div>

                  {/* Detailed Explanation */}
                  <div className="space-y-6">
                    <div className="bg-sand-light/30 border border-sand-medium/20 rounded-lg p-6">
                      <h4 className="font-display text-xl font-normal text-petrol-dark mb-3">
                        {lang === 'da' ? 'Hvad betyder din score?' : 'What does your score mean?'}
                      </h4>
                      <p className="text-sm text-text-muted leading-relaxed mb-4">
                        {lang === 'da' ? (
                          <>
                            Du har opnået en samlet score på <strong>{totalScore} ud af 72 point</strong>.
                            {scoreCategory === 'high' && ' Dette er en høj score, hvilket indikerer en betydelig forekomst af symptomer, der er typiske for voksne med ADHD.'}
                            {scoreCategory === 'medium' && ' Dette er en moderat score, hvilket indikerer, at du oplever en del udfordringer i hverdagen, som kan være relateret til ADHD.'}
                            {scoreCategory === 'low' && ' Dette er en lav score, hvilket tyder på, at dine symptomer sandsynligvis ikke skyldes ADHD, omend det ikke helt kan udelukkes.'}
                          </>
                        ) : (
                          <>
                            You achieved an overall score of <strong>{totalScore} out of 72 points</strong>.
                            {scoreCategory === 'high' && ' This is a high score, indicating a prominent presence of symptoms typical for adults with ADHD.'}
                            {scoreCategory === 'medium' && ' This is a moderate score, indicating that you experience some everyday challenges that may be related to ADHD.'}
                            {scoreCategory === 'low' && ' This is a low score, suggesting your symptoms are unlikely to be primarily caused by ADHD, though it cannot be entirely ruled out.'}
                          </>
                        )}
                      </p>

                      {/* Official WHO Part A Screening Result */}
                      <div className="border-t border-sand-medium/20 pt-4 mt-4">
                        <h5 className="text-xs font-bold uppercase tracking-wider text-petrol-dark mb-2">
                          {lang === 'da' ? 'Officiel WHO Del A Screening-indikator' : 'Official WHO Part A Screening Indicator'}
                        </h5>
                        <p className="text-sm text-text-muted leading-relaxed">
                          {lang === 'da' ? (
                            <>
                              Ud af de 6 mest prædikative spørgsmål i <strong>Del A</strong> har du besvaret{' '}
                              <strong>{partAPositiveCount} ud af 6</strong> over symptomtærsklen.
                              {partAPositiveCount >= 4 ? (
                                <span className="text-rose-700 font-semibold block mt-2">
                                  ⚠️ Da du har 4 eller flere positive svar i Del A, er denne screening POSITIV efter WHO-standarden. Dette indikerer en stærk sandsynlighed for ADHD, og en klinisk udredning anbefales.
                                </span>
                              ) : (
                                <span className="text-emerald-700 font-semibold block mt-2">
                                  ✓ Da du har færre end 4 positive svar i Del A, er denne screening NEGATIV efter WHO-standarden.
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              Out of the 6 most predictive questions in <strong>Part A</strong>, you answered{' '}
                              <strong>{partAPositiveCount} out of 6</strong> above the symptom threshold.
                              {partAPositiveCount >= 4 ? (
                                <span className="text-rose-700 font-semibold block mt-2">
                                  ⚠️ Because you answered positively to 4 or more questions in Part A, this screening is POSITIVE according to WHO guidelines. This indicates a strong likelihood of ADHD, and a clinical assessment is recommended.
                                </span>
                              ) : (
                                <span className="text-emerald-700 font-semibold block mt-2">
                                  ✓ Because you answered positively to fewer than 4 questions in Part A, this screening is NEGATIVE according to WHO guidelines.
                                </span>
                              )}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end gap-4 print:hidden">
                    <button
                      onClick={resetTest}
                      className="inline-flex items-center gap-2 px-5 py-2.5 border border-sand-medium/40 rounded-full text-sm font-semibold text-text-dark hover:bg-sand-light/30 transition-all cursor-pointer"
                    >
                      <i className="fa-solid fa-rotate-right" /> {lang === 'da' ? 'Tag testen igen' : 'Retake test'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Column: Next Steps & CTA */}
              <div className="space-y-6">
                <div className="border border-sand-medium/30 bg-white rounded-lg p-6 sm:p-8 shadow-sm">
                  <h3 className="font-display text-xl font-normal text-petrol-dark mb-4">
                    {lang === 'da' ? 'Efter screeningen' : 'After the screening'}
                  </h3>
                  <ul className="space-y-3 text-sm text-text-muted">
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-emerald-600 mt-1" />
                      <span>{lang === 'da' ? 'Dit resultat er vejledende' : 'Your result is indicative'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-emerald-600 mt-1" />
                      <span>{lang === 'da' ? 'Ingen data gemmes eller sendes' : 'No data is stored or transmitted'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-emerald-600 mt-1" />
                      <span>{lang === 'da' ? 'Brug rapporten til samtale med læge' : 'Use this report in discussion with your doctor'}</span>
                    </li>
                  </ul>

                  <div className="mt-8 bg-petrol-dark text-white rounded-lg p-5">
                    <h4 className="font-display text-lg font-normal mb-2">
                      {lang === 'da' ? 'Ønsker du en faglig vurdering?' : 'Önskar du en professionell bedömning?'}
                    </h4>
                    <p className="text-xs text-white/80 leading-relaxed mb-4">
                      {lang === 'da'
                        ? 'Book en indledende samtale, så vi sammen kan finde ud af, om en udredning er det rette for dig.'
                        : 'Boka ett inledande samtal, så kan vi tillsammans ta reda på om en utredning är rätt för dig.'}
                    </p>
                    <Link
                      to={localizePath('/kontakt')}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-petrol-dark hover:bg-sand-light rounded-full font-semibold text-xs transition-all duration-200"
                    >
                      {lang === 'da' ? 'Book indledende samtale' : 'Book initial consultation'} <i className="fa-solid fa-arrow-right" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

              {/* Right Column: After Screening & CTA */}
        <div className="mt-6 text-xs text-text-muted">
          {lang === 'da'
            ? 'ASRS-v1.1 udviklet af WHO. Kilde: Kessler RC et al. The World Health Organization Adult ADHD Self-Report Scale (ASRS): a short screening scale for use in the general population.'
            : 'ASRS-v1.1 utvecklad av WHO. Källa: Kessler RC et al. The World Health Organization Adult ADHD Self-Report Scale (ASRS): a short screening scale for use in the general population.'}
        </div>
      </main>
    </div>
  );
}
